/* Typed client for /api/history/*.
 *
 * The runner / synthesis / export modules call createInteraction() etc.
 * BEST-EFFORT — if the user isn't logged in, or the request fails,
 * we log a warn and move on. Never let history saving block the user's
 * normal flow.
 *
 * Replicable across products by changing PRODUCT (mirrors cloud-blobs.ts —
 * uses window.LP_PRODUCT if set).
 */

const PRODUCT: string =
  (typeof window !== 'undefined' && (window as any).LP_PRODUCT) || 'aiboard';

// ---------- Types matching backend/api/history.py ----------

export interface HistoryResponse {
  model_id: string;
  provider?: string | null;
  text?: string | null;
  ok: boolean;
  error?: string | null;
  duration_ms?: number | null;
  tokens_in?: number | null;
  tokens_out?: number | null;
  cost_usd?: number | null;
  metadata?: Record<string, unknown>;
}

export interface HistorySynthesis {
  mode?: string | null;
  model_id?: string | null;
  text: string;
  metadata?: Record<string, unknown>;
}

export interface HistoryDeliverable {
  kind: string;
  name: string;
  blob_id?: string | null;
  size_bytes?: number | null;
  metadata?: Record<string, unknown>;
}

export interface InteractionPayload {
  product?: string;
  prompt: string;
  selected_models: string[];
  language?: string | null;
  tags?: string[];
  metadata?: Record<string, unknown>;
  responses?: HistoryResponse[];
  synthesis?: HistorySynthesis;
  deliverables?: HistoryDeliverable[];
}

export interface InteractionFull {
  id: string;
  product: string;
  prompt_text: string;
  prompt_hash: string;
  selected_models: string[];
  language: string | null;
  tags: string[];
  metadata_json: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  responses: Array<{
    id: string;
    model_id: string;
    provider?: string | null;
    text?: string | null;
    ok: boolean;
    error?: string | null;
    duration_ms?: number | null;
    tokens_in?: number | null;
    tokens_out?: number | null;
    cost_usd?: number | null;
    metadata_json: Record<string, unknown>;
    created_at: string;
  }>;
  syntheses: Array<{
    id: string;
    mode?: string | null;
    model_id?: string | null;
    text: string;
    metadata_json: Record<string, unknown>;
    created_at: string;
  }>;
  deliverables: Array<{
    id: string;
    kind: string;
    name: string;
    blob_id?: string | null;
    size_bytes?: number | null;
    metadata_json: Record<string, unknown>;
    created_at: string;
  }>;
}

export interface InteractionRow {
  id: string;
  product: string;
  prompt_text: string;
  selected_models: string[];
  language: string | null;
  tags: string[];
  created_at: string;
  response_count: number;
  has_synthesis: boolean;
  has_deliverables: boolean;
}

export interface InteractionListResponse {
  interactions: InteractionRow[];
  total: number;
  page: number;
  page_size: number;
}

// ---------- HTTP helpers ----------

async function http<T = any>(
  path: string,
  init: RequestInit = {},
): Promise<{ ok: true; data: T } | { ok: false; status: number; message: string }> {
  try {
    const res = await fetch(`/api/history${path}`, {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(init.headers || {}) },
      ...init,
    });
    if (res.status === 204) return { ok: true, data: undefined as any };
    const text = await res.text();
    let body: any = null;
    if (text) {
      try { body = JSON.parse(text); } catch { body = text; }
    }
    if (!res.ok) {
      const message =
        (body && typeof body === 'object' && (body.detail?.message || body.detail)) ||
        (typeof body === 'string' ? body : `HTTP ${res.status}`);
      return { ok: false, status: res.status, message: String(message) };
    }
    return { ok: true, data: body as T };
  } catch (err: any) {
    return { ok: false, status: 0, message: err?.message || 'Network error' };
  }
}

// ---------- Public API ----------

/** Create a new interaction. Returns the saved record (with server-assigned id)
 *  or null on auth/network failure (caller decides whether to retry / log). */
export async function createInteraction(
  payload: InteractionPayload,
): Promise<InteractionFull | null> {
  const body: InteractionPayload = { product: PRODUCT, ...payload };
  const res = await http<InteractionFull>('/interaction', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    // 401 = not logged in → silent skip (normal). Other errors get a warn.
    if (res.status !== 401) {
      console.warn(`history.createInteraction failed: ${res.status} ${res.message}`);
    }
    return null;
  }
  return res.data;
}

export async function listInteractions(opts: {
  q?: string;
  product?: string;
  model_id?: string;
  page?: number;
  page_size?: number;
} = {}): Promise<InteractionListResponse | null> {
  const params = new URLSearchParams();
  if (opts.q)         params.set('q', opts.q);
  if (opts.product)   params.set('product', opts.product);
  if (opts.model_id)  params.set('model_id', opts.model_id);
  if (opts.page)      params.set('page', String(opts.page));
  if (opts.page_size) params.set('page_size', String(opts.page_size));
  const qs = params.toString();
  const res = await http<InteractionListResponse>(`/interactions${qs ? '?' + qs : ''}`);
  return res.ok ? res.data : null;
}

export async function getInteraction(id: string): Promise<InteractionFull | null> {
  const res = await http<InteractionFull>(`/interactions/${encodeURIComponent(id)}`);
  return res.ok ? res.data : null;
}

export async function appendSynthesis(
  interactionId: string,
  payload: HistorySynthesis,
): Promise<boolean> {
  const res = await http(`/interactions/${encodeURIComponent(interactionId)}/synthesis`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  if (!res.ok && res.status !== 401) {
    console.warn(`history.appendSynthesis failed: ${res.status} ${res.message}`);
  }
  return res.ok;
}

export async function appendDeliverable(
  interactionId: string,
  payload: HistoryDeliverable,
): Promise<boolean> {
  const res = await http(`/interactions/${encodeURIComponent(interactionId)}/deliverable`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  if (!res.ok && res.status !== 401) {
    console.warn(`history.appendDeliverable failed: ${res.status} ${res.message}`);
  }
  return res.ok;
}

export async function deleteInteraction(id: string): Promise<boolean> {
  const res = await http(`/interactions/${encodeURIComponent(id)}`, { method: 'DELETE' });
  return res.ok;
}
